import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Spacer, Button, ButtonContainer, Grid, GridColumn, GridItem, Icon, IconButton, SmartSkeleton, useSnackbar, Typography } from '@emburse/embark-core';
import { InfoIcon, LaunchIcon, SyncIcon } from '@emburse/embark-icons';
import { useState, useEffect } from 'react';
import { FileUploadButton, IconPopover } from '../../components';
import SingleColumnLayout from '../../components/Layouts/SingleColumnLayout';
import SSITable from './SSITable';
import SSIModal from './SSIModal';
import { checkInProgress, createSSIRequest, getCurrentRequests, getTemplate, validateFile } from '../../api/SSI';
import { $t, useTranslation } from '../../hooks/useTranslation';
import useAccountConfig from '../../hooks/useAccountConfig';
const companyName = 'Certify, Inc.';
const defaultValidationResults = {
    deptCount: 0,
    empCount: 0,
    expCatCount: 0,
    gldCount: 0,
    deptMessage: [],
    empMessage: [],
    expCatMessage: [],
    gldMessage: [],
    fatalErrorMessage: []
};
const transformRequests = (row, i) => ({
    ...row,
    id: i
});
const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
});
function FileUploader({ setRequestLoading, setShowModal, setUploadFile, setValidationErrors, setValidationResults, uploadDisabled }) {
    useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { accountConfig } = useAccountConfig();
    const handleValidationResults = (results) => {
        let hasErrors = false;
        if (results.expCatMessage.length > 0) {
            hasErrors = true;
        }
        if (results.deptMessage.length > 0) {
            hasErrors = true;
        }
        if (results.empMessage.length > 0) {
            hasErrors = true;
        }
        if (results.gldMessage.length > 0) {
            hasErrors = true;
        }
        if (results.fatalErrorMessage.length > 0) {
            hasErrors = true;
        }
        setValidationResults(results);
        setValidationErrors(hasErrors);
    };
    return (_jsxs(_Fragment, { children: [_jsx(FileUploadButton, { variant: "contained", size: "large", disabled: uploadDisabled, accept: ".xlsx", onFileSelect: async (event) => {
                    const file = event.target.files[0];
                    const fileData = (await toBase64(file));
                    validateFile(fileData).then((response) => {
                        if (response.Status === 'error') {
                            enqueueSnackbar(response.Message, {
                                variant: 'alert',
                                AlertProps: { severity: 'error' }
                            });
                        }
                        else {
                            setRequestLoading(true);
                            setShowModal(true);
                            handleValidationResults(JSON.parse(response.Data));
                            setUploadFile(fileData);
                            setRequestLoading(false);
                        }
                    });
                }, children: "Upload" }), accountConfig.Emp.Company.Name === companyName && (_jsx(IconPopover, { title: $t('More information'), content: $t('Please masquerade into a company in order to upload a new template.'), iconUrl: InfoIcon, edge: false }))] }));
}
function SingleStepImplementation() {
    useTranslation();
    const { accountConfig } = useAccountConfig();
    const [requestLoading, setRequestLoading] = useState(false);
    const [requests, setRequests] = useState([]);
    const [tableLoading, setTableLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [validationErrors, setValidationErrors] = useState(false);
    const [validationResults, setValidationResults] = useState(defaultValidationResults);
    const [uploadFile, setUploadFile] = useState('');
    const [uploadDisabled, setUploadDisabled] = useState(accountConfig.Emp.Company.Name === companyName);
    const [uploadLoading, setUploadLoading] = useState(true);
    const { enqueueSnackbar } = useSnackbar();
    const GetCurrentRequests = () => {
        setTableLoading(true);
        getCurrentRequests().then((resp) => {
            setRequests([...resp.map(transformRequests)]);
            setTableLoading(false);
        });
    };
    const CheckUpload = () => {
        checkInProgress().then((resp) => {
            if (resp.Status === 'InProgress') {
                setUploadDisabled(true);
            }
            setUploadLoading(false);
        });
    };
    const initialChecks = () => {
        GetCurrentRequests();
        CheckUpload();
    };
    const closeModalHandler = () => {
        // Reset ValidationResults
        setValidationResults(defaultValidationResults);
        setShowModal(false);
    };
    const confirmModalHandler = () => {
        // TODO Finish the rest of this in CER-12570.
        setShowModal(false);
        // This needs to pass the file to the API call.
        createSSIRequest(uploadFile).then((response) => {
            const errorFound = response.Status === 'error';
            enqueueSnackbar(errorFound ? response.Message : $t('Request created'), {
                variant: 'alert',
                AlertProps: { severity: errorFound ? 'error' : 'success' }
            });
        });
    };
    const downloadTemplate = () => {
        getTemplate().then((response) => {
            if (response.Status === 'error') {
                enqueueSnackbar(response.Message, {
                    variant: 'alert',
                    AlertProps: { severity: 'error' }
                });
            }
            else {
                const responseData = JSON.parse(response.Data);
                const fileData = responseData.FileData;
                const fileName = responseData.FileName;
                const link = document.createElement('a');
                link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${encodeURIComponent(fileData)}`;
                link.setAttribute('download', fileName);
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        });
    };
    /**
    const filteredRequests = () => {
        if (window.AccountConfig.UIFlags.IsMasquerading) {
            return requests.filter((item) => item.CompanyID === window.AccountConfig.Emp.Company.PK);
        }
        return requests;
    };
    */
    useEffect(initialChecks, []);
    return (_jsxs(SingleColumnLayout, { children: [_jsx(SSIModal, { open: showModal, isLoading: requestLoading, errorsFound: validationErrors, validationResults: validationResults, onClose: closeModalHandler, onConfirm: confirmModalHandler }), _jsxs(GridColumn, { spacing: 1, children: [_jsx(GridItem, { children: _jsx(Typography, { variant: "h4", gutterBottom: true, children: $t('Single Step Implementation') }) }), _jsx(GridItem, { xs: 12, sm: 12, md: 10, children: _jsx(Typography, { variant: "body1", gutterBottom: true, children: $t(`The single step implementation tool is used to automatically implement a new company.
                        Download a template excel file below, and have the client administrator fill out the file with all the necessary data.
                        Once this has been completed, and reviewed, upload the file to be placed into our implementation queue.`) }) }), _jsx(GridItem, { children: _jsx(Button, { variant: "text", color: "primary", startIcon: _jsx(Icon, { iconUrl: LaunchIcon }), onClick: () => window.open('https://chromeriver.atlassian.net/wiki/spaces/CTD/pages/2603155511/Single+Step+Implementation', '_blank'), children: $t('Help Center Documentation') }) })] }), _jsx(Spacer, { size: 48 }), _jsxs(Grid, { container: true, spacing: 1, direction: "row", justifyContent: "space-between", md: 10, children: [_jsx(Grid, { item: true, children: _jsx(Typography, { variant: "h5", gutterBottom: true, children: $t('Upload') }) }), _jsx(SmartSkeleton, { loading: uploadLoading, children: _jsx(Grid, { item: true, children: _jsxs(ButtonContainer, { alignButtons: "right", children: [_jsx(Button, { variant: "outlined", size: "large", onClick: downloadTemplate, children: $t('Download Template') }), FileUploader({
                                        setRequestLoading,
                                        setShowModal,
                                        setUploadFile,
                                        setValidationErrors,
                                        setValidationResults,
                                        uploadDisabled
                                    })] }) }) })] }), _jsx(Spacer, { size: 48 }), _jsxs(Grid, { container: true, spacing: 1, direction: "row", justifyContent: "space-between", md: 10, children: [_jsx(Grid, { item: true, children: _jsx(Typography, { variant: "h5", gutterBottom: true, children: $t('Status') }) }), _jsx(Grid, { item: true, children: _jsx(IconButton, { size: "medium", onClick: GetCurrentRequests, children: _jsx(Icon, { iconUrl: SyncIcon }) }) })] }), _jsx(Spacer, { size: 24 }), _jsx(Grid, { md: 10, children: _jsx(Grid, { item: true, children: _jsx(SSITable, { loading: tableLoading, requests: requests }) }) })] }));
}
export default SingleStepImplementation;
